import React, { useCallback, useMemo } from 'react';
import { View, Text, Pressable, Share, Platform, ScrollView, Image, useWindowDimensions } from 'react-native';
import { useCompetitionId, useCompetitionBasePath } from '../../../src/competitions/contexts/CompetitionContext';
import { competitionGroupsRoute, competitionGroupRoute } from '../../../src/competitions/helpers/competitionRoutes.helper';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import { Copy, Share2 } from 'lucide-react-native';
import { buildCompetitionSquadJoinUrl } from '../../../src/competitions/helpers/competitionInviteLink.helper';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useSnackbar } from '../../../src/shared/hooks/useSnackbar';
import { copyText } from '../../../src/shared/helpers/clipboard.helper';
import { ARENA_EMPTY_MONKEY_SOURCE, IDLE_COIN_SOURCE } from '../../../constants/assets';
import { WorldCupCrestPreview } from '../WorldCupCrestPreview';
import { getWorldCupGroupCreatedScreenStyles } from './WorldCupGroupCreatedScreen.styles';
import { buildShareMessage, getShareDialogTitle } from '../GroupHeroBanner/GroupHeroBanner.constants';
import { WC_GROUP_CREATED_COPY } from './WorldCupGroupCreatedScreen.constants';
import type { CrestColorId, CrestEmblemId } from '../CreateWorldCupGroupScreen/CreateWorldCupGroupScreen.constants';

interface WorldCupGroupCreatedScreenProps {
  groupId: string;
  inviteCode: string;
  groupName: string;
  crestColor: CrestColorId;
  crestEmblem: CrestEmblemId;
  description: string;
  entryFeeCoins: number;
}

const CONFETTI_COLORS = ['#FCD34D', '#A78BFA', '#C4B5FD', '#4ADE9B', '#FF8A65', '#5BB8FF'] as const;
const CONFETTI_BITS = Array.from({ length: 20 }).map((_, i) => ({
  left: `${(i * 37 + 13) % 100}%` as const,
  top: `${(i * 53 + 7) % 100}%` as const,
  width: 6 + (i % 4) * 2,
  height: i % 3 === 2 ? 6 + (i % 4) * 2 : Math.round((6 + (i % 4) * 2) * 0.4),
  color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  borderRadius: i % 3 === 0 ? 0 : i % 3 === 1 ? 2 : 50,
  rotation: (i * 41) % 360,
}));

export const WorldCupGroupCreatedScreen: React.FC<WorldCupGroupCreatedScreenProps> = ({
  groupId,
  inviteCode,
  groupName,
  crestColor,
  crestEmblem,
  description,
  entryFeeCoins,
}) => {
  const theme = useTheme();
  const router = useRouter();
  const basePath = useCompetitionBasePath();
  const competitionId = useCompetitionId();
  const { height: windowHeight } = useWindowDimensions();
  const compact = windowHeight < 740;
  const { showSnackbar } = useSnackbar();
  const styles = useMemo(
    () =>
      getWorldCupGroupCreatedScreenStyles(theme, {
        compact,
        bottomInset: 16,
      }),
    [theme, compact],
  );

  const backdropGradientColors = useMemo(
    () => [theme.background.primary, `${theme.primaryDark}22`] as const,
    [theme],
  );
  const heroGradientColors = useMemo(
    () => [`${theme.primaryDark}55`, theme.background.card] as const,
    [theme],
  );
  const inviteGradientColors = useMemo(
    () => [`${theme.primaryDark}55`, theme.background.card] as const,
    [theme],
  );
  const ctaFadeColors = useMemo(
    () => ['transparent', theme.background.primary] as const,
    [theme],
  );
  const ctaGradientColors = useMemo(
    () => [theme.primaryDark, theme.primary] as const,
    [theme],
  );
  const shareGradientColors = useMemo(
    () => [theme.primaryDark, theme.primary] as const,
    [theme],
  );

  const inviteLink = useMemo(() => buildCompetitionSquadJoinUrl(competitionId, inviteCode), [competitionId, inviteCode]);

  const handleCopyCode = useCallback(async () => {
    try {
      await copyText(inviteCode);
      showSnackbar(WC_GROUP_CREATED_COPY.CODE_COPIED, 'success');
    } catch {
      /* user cancelled */
    }
  }, [inviteCode, showSnackbar]);

  const handleShare = useCallback(async () => {
    try {
      const shareMessage = buildShareMessage(groupName, inviteLink);
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ title: getShareDialogTitle(), text: shareMessage });
      } else {
        await Share.share({ message: shareMessage });
      }
      showSnackbar(WC_GROUP_CREATED_COPY.LINK_COPIED, 'success');
    } catch {
      /* user cancelled */
    }
  }, [groupName, inviteLink, showSnackbar]);

  const handleViewSquad = useCallback(() => {
    router.replace(competitionGroupRoute(basePath, groupId) as never);
  }, [basePath, groupId, router]);

  const handleBackToGroups = useCallback(() => {
    router.replace(competitionGroupsRoute(basePath) as never);
  }, [router]);

  const hoverWeb = Platform.select({ web: WEB_HOVER_TRANSITION_STYLE });

  return (
    <View style={styles.container}>
      <LinearGradient
        pointerEvents="none"
        colors={backdropGradientColors}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.gradientBackdrop}
      />
      <View style={styles.bokehLarge} pointerEvents="none" />
      <View style={styles.bokehSmall} pointerEvents="none" />
      <View style={styles.bokehAccent} pointerEvents="none" />

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Hero card — mascot + confetti */}
        <View style={styles.heroCard}>
          <LinearGradient
            colors={heroGradientColors}
            start={{ x: 0.5, y: 0 }}
            end={{ x: 0.5, y: 1 }}
            style={styles.heroGradient}
            pointerEvents="none"
          />
          <View style={styles.confettiContainer} pointerEvents="none">
            {CONFETTI_BITS.map((bit, i) => (
              <View
                key={i}
                style={[
                  styles.confettiBit,
                  {
                    left: bit.left,
                    top: bit.top,
                    width: bit.width,
                    height: bit.height,
                    backgroundColor: bit.color,
                    borderRadius: bit.borderRadius,
                    transform: [{ rotate: `${bit.rotation}deg` }],
                  },
                ]}
              />
            ))}
          </View>
          <View style={styles.mascotGlow} pointerEvents="none" />
          <Image source={ARENA_EMPTY_MONKEY_SOURCE} style={styles.mascotImage} resizeMode="contain" />
          <Text style={styles.badge}>{WC_GROUP_CREATED_COPY.BADGE}</Text>
          <Text style={styles.headline} numberOfLines={3} adjustsFontSizeToFit minimumFontScale={0.78}>
            {groupName} {WC_GROUP_CREATED_COPY.IS_LIVE}
          </Text>
          <Text style={styles.subtitle}>{WC_GROUP_CREATED_COPY.SUBTITLE}</Text>
        </View>

        {/* Crest + summary row */}
        <View style={styles.crestSummaryCard}>
          <WorldCupCrestPreview colorId={crestColor} emblemId={crestEmblem} name={groupName} size={56} />
          <View style={styles.crestSummaryInfo}>
            <Text style={styles.crestSummaryName} numberOfLines={1}>{groupName}</Text>
            <Text style={styles.crestSummaryTagline} numberOfLines={1}>
              {description.length > 0 ? description : 'Predict together · share the pot'}
            </Text>
          </View>
          <View style={styles.crestSummaryBuyInWrap}>
            <Text style={styles.crestSummaryBuyInLabel}>Buy-in</Text>
            {entryFeeCoins > 0 ? (
              <View style={styles.crestSummaryBuyInCoinRow}>
                <Image source={IDLE_COIN_SOURCE} style={styles.crestSummaryBuyInCoin} />
                <Text style={styles.crestSummaryBuyInAmount}>{entryFeeCoins.toLocaleString()}</Text>
              </View>
            ) : (
              <Text style={styles.crestSummaryBuyInFree}>Free</Text>
            )}
          </View>
        </View>

        {/* Invite card — code + share row */}
        <View style={styles.inviteCard}>
          <LinearGradient
            colors={inviteGradientColors}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.inviteGradient}
            pointerEvents="none"
          />
          <View style={styles.inviteGlow} pointerEvents="none" />
          <View style={styles.inviteInner}>
            <Text style={styles.codeLabel}>{WC_GROUP_CREATED_COPY.INVITE_CODE_LABEL}</Text>
            <Text style={styles.codeText} selectable numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.65}>
              {inviteCode}
            </Text>
            <View style={styles.shareRow}>
              <Pressable
                style={({ pressed, hovered }) => [
                  styles.shareButton,
                  hoverWeb,
                  pressed && styles.shareButtonPressed,
                  hovered && Platform.OS === 'web' && styles.shareButtonHover,
                ]}
                onPress={() => { void handleCopyCode(); }}
                accessibilityLabel={WC_GROUP_CREATED_COPY.COPY_CODE_A11Y}
              >
                <Copy size={13} color={theme.gray[900]} strokeWidth={2} />
                <Text style={styles.shareButtonText}>{WC_GROUP_CREATED_COPY.COPY_CODE}</Text>
              </Pressable>
              <Pressable
                style={({ pressed, hovered }) => [
                  styles.shareButton,
                  styles.shareButtonPrimary,
                  hoverWeb,
                  pressed && styles.shareButtonPressed,
                  hovered && Platform.OS === 'web' && styles.shareButtonHover,
                ]}
                onPress={() => { void handleShare(); }}
              >
                <LinearGradient
                  colors={shareGradientColors}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={styles.shareButtonGradient}
                />
                <Share2 size={13} color={theme.onPrimary} strokeWidth={2} />
                <Text style={styles.shareButtonTextPrimary}>{WC_GROUP_CREATED_COPY.SHARE_LINK}</Text>
              </Pressable>
            </View>
          </View>
        </View>

        <Pressable
          style={({ pressed }) => [styles.tertiaryCta, pressed && styles.tertiaryCtaPressed]}
          onPress={handleBackToGroups}
        >
          <Text style={styles.tertiaryCtaText}>{WC_GROUP_CREATED_COPY.BACK_TO_GROUPS}</Text>
        </Pressable>
      </ScrollView>

      {/* Sticky CTA */}
      <View pointerEvents="box-none" style={styles.stickyCtaWrap}>
        <LinearGradient
          pointerEvents="none"
          colors={ctaFadeColors}
          style={styles.stickyCtaFade}
        />
        <Pressable
          style={({ pressed }) => [
            styles.stickyCtaButton,
            hoverWeb,
            pressed && styles.stickyCtaPressed,
          ]}
          onPress={handleViewSquad}
        >
          <LinearGradient
            colors={ctaGradientColors}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={styles.stickyCtaGradient}
          >
            <Text style={styles.stickyCtaText}>{WC_GROUP_CREATED_COPY.VIEW_SQUAD}</Text>
          </LinearGradient>
        </Pressable>
      </View>
    </View>
  );
};
